import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  PUB_DRAFTS_KEY,
  type PublicationDraft,
  type PublicationDraftAuthor,
  type PublicationDraftIdentifier,
  type PublicationDraftSection,
  type PublicationDraftStore,
} from '@/lib/publicationDrafts';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/**
 * localStorage persistence for publication drafts — same module-level
 * cache + subscriber pattern as `useArticleDrafts`, so the editor's
 * save control, the drafts list, and the Write menu count all stay
 * consistent the moment anything is saved or deleted.
 */
let cache: PublicationDraftStore | undefined;
const listeners = new Set<() => void>();

function getSnapshot(): PublicationDraftStore {
  // Must be referentially stable between changes, or
  // useSyncExternalStore re-renders forever.
  cache ??= readPublicationDrafts();
  return cache;
}

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key !== PUB_DRAFTS_KEY) return;
    cache = undefined;
    emit();
  });
}

function mutate(update: (store: PublicationDraftStore) => PublicationDraftStore): void {
  const next = update(getSnapshot());
  // Write first: if storage is full this throws, and the in-memory
  // cache must not claim a save that didn't actually persist.
  writePublicationDrafts(next);
  cache = next;
  emit();
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Reads and re-validates every stored publication draft.
 *
 * localStorage is user- and extension-writable and may hold data from an
 * older version of the app, so nothing here is trusted: a malformed
 * entry is dropped rather than allowed to load into the editor as
 * `undefined` and blank out real work. In particular `sections`,
 * `authors`, and `identifiers` must survive as arrays — the editor maps
 * over all three, and a non-array would crash the page on resume.
 */
function readPublicationDrafts(): PublicationDraftStore {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PUB_DRAFTS_KEY);
  } catch {
    // Storage can be unavailable entirely (Safari private mode, etc.).
    return {};
  }
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const out: PublicationDraftStore = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const draft = value as Partial<PublicationDraft>;
      if (typeof draft.id !== 'string' || !draft.id) continue;
      if (typeof draft.pubkey !== 'string' || !draft.pubkey) continue;

      out[key] = {
        id: draft.id,
        pubkey: draft.pubkey,
        ...(typeof draft.d === 'string' && draft.d ? { d: draft.d } : {}),
        title: str(draft.title),
        summary: str(draft.summary),
        type: str(draft.type) || 'book',
        image: str(draft.image),
        version: str(draft.version),
        publishedOn: str(draft.publishedOn),
        publishedBy: str(draft.publishedBy),
        source: str(draft.source),
        topics: str(draft.topics),
        authors: Array.isArray(draft.authors)
          ? draft.authors
              .filter((a): a is PublicationDraftAuthor => Boolean(a) && typeof a === 'object')
              .map((a) => ({ key: str(a.key) || nextLocalKey(), name: str(a.name), role: str(a.role) }))
          : [],
        identifiers: Array.isArray(draft.identifiers)
          ? draft.identifiers
              .filter((i): i is PublicationDraftIdentifier => Boolean(i) && typeof i === 'object')
              .map((i) => ({ key: str(i.key) || nextLocalKey(), type: str(i.type) || 'isbn', value: str(i.value) }))
          : [],
        sections: Array.isArray(draft.sections)
          ? draft.sections
              .filter((s): s is PublicationDraftSection => Boolean(s) && typeof s === 'object')
              .map((s) => ({
                key: str(s.key) || nextLocalKey(),
                d: str(s.d),
                title: str(s.title),
                content: str(s.content),
                format: typeof s.format === 'string' ? s.format : undefined,
                published: s.published === true,
              }))
          : [],
        createdAt: num(draft.createdAt),
        updatedAt: num(draft.updatedAt),
      };
    }
    return out;
  } catch {
    return {};
  }
}

let localKeyCounter = 0;
/** Replacement React list key for an entry whose stored key was lost or malformed. */
function nextLocalKey(): string {
  localKeyCounter += 1;
  return `restored-${localKeyCounter}`;
}

function writePublicationDrafts(store: PublicationDraftStore): void {
  try {
    localStorage.setItem(PUB_DRAFTS_KEY, JSON.stringify(store));
  } catch (error) {
    if (error instanceof DOMException) throw new Error('Not enough browser storage left to save this draft.');
    throw error;
  }
}

export interface UsePublicationDraftsResult {
  drafts: PublicationDraft[];
  count: number;
  get: (id: string | undefined) => PublicationDraft | undefined;
  save: (draft: PublicationDraftInput, existingId?: string) => string;
  remove: (id: string) => void;
  clear: () => void;
}

interface PublicationDraftInput {
  /** The `d` tag of the publication being revised, or undefined for a new one. */
  d?: string;
  title: string;
  summary: string;
  type: string;
  image: string;
  version: string;
  publishedOn: string;
  publishedBy: string;
  source: string;
  topics: string;
  authors: PublicationDraftAuthorInput[];
  identifiers: PublicationDraftIdentifierInput[];
  sections: PublicationDraftSectionInput[];
}

interface PublicationDraftAuthorInput {
  key: string;
  name: string;
  role: string;
}

interface PublicationDraftIdentifierInput {
  key: string;
  type: string;
  value: string;
}

interface PublicationDraftSectionInput {
  key: string;
  d: string;
  title: string;
  content: string;
  format: string | undefined;
  published: boolean;
}

/**
 * Local, unpublished publication drafts — a whole NKBIP-01 publication
 * in progress (metadata, bibliographic details, and every section)
 * kept only on this device and scoped to the logged-in Nostr identity.
 *
 * Unlike article drafts, a publication draft is not yet synced to Nostr:
 * it holds an arbitrary number of sections, so an encrypted-rumor
 * payload would be far larger than a single article's, and there's no
 * merge story yet for a section list edited on two devices. Local-only
 * is the honest behavior until that's designed, rather than a sync that
 * silently drops sections.
 */
export function usePublicationDrafts(): UsePublicationDraftsResult {
  const store = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const { user } = useCurrentUser();
  const pubkey = user?.pubkey;

  const drafts = useMemo(
    () =>
      Object.values(store)
        .filter((draft) => !pubkey || draft.pubkey === pubkey)
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [store, pubkey],
  );

  const save = useCallback(
    (input: PublicationDraftInput, existingId?: string): string => {
      if (!pubkey) throw new Error('Must be logged in to save a draft.');

      const existing = existingId ? store[existingId] : undefined;
      const id = existing && existing.pubkey === pubkey ? existing.id : randomUUIDSafe();
      const now = Date.now();

      const draft: PublicationDraft = {
        id,
        pubkey,
        // A `d` already on the draft is preserved even if this save
        // doesn't supply one: it identifies a real published
        // publication, and dropping it would turn later saves into a
        // fork rather than a revision.
        ...(input.d ?? existing?.d ? { d: input.d ?? existing?.d } : {}),
        title: input.title,
        summary: input.summary,
        type: input.type,
        image: input.image,
        version: input.version,
        publishedOn: input.publishedOn,
        publishedBy: input.publishedBy,
        source: input.source,
        topics: input.topics,
        authors: input.authors,
        identifiers: input.identifiers,
        sections: input.sections,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      };

      mutate((current) => ({
        ...current,
        [draft.id]: draft,
      }));
      return draft.id;
    },
    [store, pubkey],
  );

  const remove = useCallback((id: string) => {
    mutate((current) => {
      if (!(id in current)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    if (!pubkey) return;
    mutate((current) => {
      const next: PublicationDraftStore = {};
      for (const [key, draft] of Object.entries(current)) {
        if (draft.pubkey !== pubkey) next[key] = draft;
      }
      return next;
    });
  }, [pubkey]);

  const get = useCallback(
    (id: string | undefined) => {
      if (!id) return undefined;
      const draft = store[id];
      // Never hand back another identity's draft to the current session.
      if (!draft || (pubkey && draft.pubkey !== pubkey)) return undefined;
      return draft;
    },
    [store, pubkey],
  );

  return { drafts, count: drafts.length, get, save, remove, clear };
}

function randomUUIDSafe(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
